
import Vue from 'vue';
import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { initializeStore, SeamlessCoreStore, Subscription } from '@seamless/store';
import { MediaQuery, MediaQueryChangeEvent, MediaQueryService } from '@workbench/core';
import { SVRTypes } from '@/svr.d';
import { LinkUtils } from './helpers/LinkUtils';
import { connectionName } from '@owc/connection-aem-environment';

declare global {
  interface Window {
    matchMedia: any;
  }
}

const inputErrorMap: SVRTypes.InputErrorMap = {
  IDENTIFIER_NOT_FOUND: {
    message: 'inputNotFoundMessage',
  },
  NO_VAN_BAUMUSTER: {
    message: 'inputBaumusterMessage',
    appendNBA: true,
  },
  INVALID_IDENTIFIER: {
    message: 'inputValidationMessage',
  },
};

const errorNotificationMap: SVRTypes.NotificationErrorMap = {
  NO_SALES_REFERENCE_FOR_ENGINE: {
    message: 'inputNoRecommendationMessage',
    appendNBA: true,
    headline: 'inputNoRecommendationHeadline',
  },
  NO_VCS_CONNECTION: {
    message: 'inputGeneralErrorMessage',
    appendNBA: true,
    headline: 'inputGeneralErrorHeadline',
  },
  API: {
    message: 'inputGeneralErrorMessage',
    appendNBA: true,
    headline: 'inputGeneralErrorHeadline',
  },
};

const DARK_THEME = 'vans_theme-dark';

export default Vue.extend({
  name: 'vans-app',
  props: {},
  data(): SVRTypes.ComponentData {
    return {
      linkUtils: new LinkUtils(),
      vinInput: '',
      checked: false,
      showErrorMessage: false,
      validationMessage: '',
      errorNotification: '',
      imageRatio: '3x4',
      isDarkTheme: false,
      theme: 'light',
      isRtl: false,
      inputTouched: false,
      button: {
        loading: false,
        disabled: true,
      },
      reversePosition: false,
      dataFetched: false,
      vanalyticsEndpoint: {
        dev: 'https://svr.devm-publish.vans.aem.oneweb.mercedes-benz.com/vans/bin/daimler/vanalytics-api/successorVehicle',
        int: 'https://svr.intp-publish.vans.aem.oneweb.mercedes-benz.com/vans/bin/daimler/vanalytics-api/successorVehicle',
        prod: 'https://www.vans.mercedes-benz.com/vans/bin/daimler/vanalytics-api/successorVehicle',
      },
      store: {} as SeamlessCoreStore,
      subscription: {} as Subscription,
      stage: 'prod',
      aemPayload: {
        headline: 'headline',
        headlineTag: 'h2',
        copyStartText: 'copyStartText',
        tooltipLinkText: 'tooltipLinkText',
        copyEndText: 'copyEndText',
        tooltipText: 'tooltipText',
        tooltipBulletPointList: [
          {
            bulletPointText: 'bulletPointText',
          },
        ],
        privacyLinkLabel: null,
        privacyLink: null,
        inputLabel: 'inputLabel',
        inputErrorMessage: 'inputErrorMessage',
        inputValidationMessage: 'inputValidationMessage',
        inputNotFoundMessage: 'inputNotFoundMessage',
        inputBaumusterMessage: 'inputBaumusterMessage',
        inputNoRecommendationHeadline: 'inputNoRecommendationHeadline',
        inputNoRecommendationMessage: 'inputNoRecommendationMessage',
        inputGeneralErrorHeadline: 'inputGeneralErrorHeadline',
        inputGeneralErrorMessage: 'inputGeneralErrorMessage',
        NBAText: 'NBAText',
        linkNBA: 'linkNBA',
        NBALinkLabel: 'NBALinkLabel',
        NBALinkTargetBlank: true,
        checkboxLabel: 'checkboxLabel',
        submitButtonText: 'submitButtonText',
        vocUrl: 'https://voc-int.mercedes-benz.com/voc/onlinecode/<onlinecode>',
        imageMq1: 'https://source.unsplash.com/X7OdIjzkX48/400x400',
        imageMq5: 'https://source.unsplash.com/8iCdRKKoG7g/400x400',
        imageAlt: 'imageAlt',
        imageTitle: 'imageTitle',
        imagePlacement: 'left',
      },
    };
  },
  created(): void {
    this.store = initializeStore();
  },
  mounted(): void {
    this.subscription = this.store.subscribe(connectionName, (state: SVRTypes.VansConnectionAemEnvironmentState) => {
      this.stage = state['svr-environment'] || 'prod';
    });
    this.initImageRatio();
    this.initParams();
    this.setTheme();
    this.setRTL();

    //force update to init component registry
    this.$forceUpdate();
  },
  updated(): void {
    this.initComponentRegistry();
  },
  methods: {
    initComponentRegistry() {
      if (this.dataFetched) {
        return;
      }

      const componentRegistry = initializeComponentRegistry();
      const componentChangeListener = (componentJson: any) => {
        this.initData(componentJson.payload);
      };
      const rootElement = (this.$el.getRootNode() as any).host;
      const componentId: string = rootElement?.getAttribute('component-id') || 'notFound';

      componentRegistry.subscribe(componentId, componentChangeListener);
      this.dataFetched = true;
    },
    initImageRatio() {
      const myMediaQueryService = new MediaQueryService(window);
      const currentMediaQuery: MediaQuery = myMediaQueryService.getCurrentMediaQuery();

      this.changeImageRatio(currentMediaQuery);

      // to observe media query changes across time
      window.addEventListener('wbresize', ((e: CustomEvent<MediaQueryChangeEvent>) => {
        this.changeImageRatio(e.detail.current);
      }) as any);
    },
    initData(payload: SVRTypes.AEMPayload) {
      this.setPayload(payload);
      this.setImagePosition();
    },
    initParams() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const scrollParam = urlParams.get('cmp');
      if (scrollParam && scrollParam === 'svr') {
        /* need a timeout here since without it offsetTop returns 0 and if content above the svr needs longer to load
        the scroll position is lost*/
        window.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => {
            (this.$refs.outerRef as HTMLDivElement).scrollIntoView();
          }, 400);
        });
      }
      const vinParam = urlParams.get('vin');
      if (vinParam) {
        this.vinInput = vinParam;
        if (!this.isInputValid(vinParam) || !this.isInputLengthValid(vinParam)) {
          this.validationMessage = this.aemPayload.inputValidationMessage;
        }
      }
    },
    setTheme() {
      const mainElements = document.getElementsByTagName('main');
      this.isDarkTheme = mainElements[0] && mainElements[0].classList?.contains(DARK_THEME);
      this.theme = this.isDarkTheme ? 'dark' : 'light';
    },
    setRTL() {
      if (document.documentElement?.getAttribute('dir') === 'rtl') {
        this.$el.setAttribute('dir', 'rtl');
        this.isRtl = true;
      }
    },
    setImagePosition() {
      const imageRight = this.aemPayload.imagePlacement === 'right';
      //XOR to position the image to right place
      this.reversePosition = this.isRtl !== imageRight;
    },
    setPayload(payload: SVRTypes.AEMPayload) {
      this.aemPayload = payload;
    },
    changeImageRatio(currentMq: string) {
      switch (currentMq) {
        case 'mq1':
        case 'mq2':
        case 'mq3':
        case 'mq4':
          this.imageRatio = '3x2';
          break;
        case 'mq5':
        case 'mq6':
          this.imageRatio = '3x4';
          break;
      }
    },
    handleInput(value: string) {
      if (!this.inputTouched) {
        this.trackEvent('start input');
        this.inputTouched = true;
      }
      if (!this.isInputValid(value)) {
        this.validationMessage = this.aemPayload.inputValidationMessage;
        return;
      }

      this.vinInput = value.toUpperCase();
      this.validationMessage = '';
      this.checkButtonEnabled();
    },
    handleInputBlur(event: FocusEvent) {
      const val = (event.target as HTMLInputElement).value;
      const valid = this.isInputLengthValid(val) && this.isInputValid(val);
      this.validationMessage = !valid ? this.aemPayload.inputValidationMessage : '';
    },
    handleCheckbox() {
      this.checkButtonEnabled();
    },
    checkButtonEnabled() {
      /* until wb 7 upgrade we need to work with a ref here since we lose reactivity on this.button.disabled after
         the button was loading for the first time */
      if (!this.checkAllValid()) {
        (this.$refs.buttonRef as HTMLButtonElement)?.setAttribute('disabled', '');
        return;
      }
      (this.$refs.buttonRef as HTMLButtonElement)?.removeAttribute('disabled');
    },
    isInputValid(value: string) {
      return /^[A-Za-z0-9]*$/.test(value);
    },
    isInputLengthValid(value: string) {
      return value.length === 17;
    },
    setButtonLoading(loading: boolean) {
      this.button.loading = loading;
    },
    checkAllValid() {
      const value = this.vinInput;
      return this.checked && this.isInputLengthValid(value) && this.isInputValid(value);
    },
    getNBA(): string {
      if (!this.aemPayload.NBALinkLabel || !this.aemPayload.linkNBA) {
        return `${this.aemPayload.NBAText}`;
      }
      const link = this.linkUtils.getPublicLink(this.aemPayload.linkNBA);
      return `${this.aemPayload.NBAText} <a class="wb-link wb-link--inline ${
        this.isDarkTheme ? 'wb-link--theme-dark' : ''
      }" href="${link}" ${this.aemPayload.NBALinkTargetBlank ? 'target="_blank" rel=noopener' : ''}>${
        this.aemPayload.NBALinkLabel
      }</a>`;
    },
    handleNBAClick(e: MouseEvent) {
      if ((e.target as HTMLButtonElement).matches('a')) {
        this.trackEvent(
          'error link click',
          this.aemPayload.NBALinkLabel,
          this.linkUtils.getPublicLink(this.aemPayload.linkNBA),
        );
      }
    },
    getValidationMessage(message: string, appendNBA = false, headline?: string): string {
      let res = message;
      if (headline) {
        res = `<h3>${headline}</h3>` + res;
      }
      if (appendNBA) {
        const nba = this.getNBA();
        if (nba.length) {
          res += ' ' + nba;
        }
      }
      return res;
    },
    trackEvent(label: string, linkLabel?: string, destinationUrl?: string) {
      window.dispatchEvent(
        new CustomEvent('successor-vehicle:tracking:click', {
          detail: {
            category: 'link',
            action: 'vehicle_recommendation',
            label: label,
            link_text: linkLabel,
            destinationUrl,
            cluster: 'website components',
          },
        }),
      );
    },
    async submitForm(event: Event) {
      event.preventDefault();
      this.errorNotification = '';
      if (!this.checkAllValid()) {
        this.validationMessage = this.aemPayload.inputValidationMessage;
        return;
      }

      try {
        this.button.loading = true;
        const res = await fetch(this.vanalyticsEndpoint[this.stage], {
          method: 'POST',
          body: JSON.stringify({
            vin: this.vinInput,
          }),
        });

        this.button.loading = false;
        if (res.ok) {
          const { referenceVehicles, errors }: SVRTypes.Response<string> = await res.json();
          if (referenceVehicles && referenceVehicles[this.vinInput]) {
            const parsedUrl = this.aemPayload.vocUrl.replace(
              '<onlinecode>',
              referenceVehicles[this.vinInput].onlineCode,
            );
            window.open(parsedUrl, '_blank');
            this.trackEvent('submission successful', this.aemPayload.submitButtonText);
            this.vinInput = '';
          } else {
            this.vinInput = '';
            if (errors && errors[0]) {
              this.handleVINErrors(errors);
            }
          }
        } else {
          throw Error(`Http Error Response ${res.statusText}`);
        }
      } catch (e) {
        this.vinInput = '';
        this.button.loading = false;
        this.errorNotification = this.getValidationMessage(
          this.aemPayload.inputGeneralErrorMessage,
          true,
          this.aemPayload.inputGeneralErrorHeadline,
        );
        this.trackEvent(`Error: ${this.aemPayload.inputGeneralErrorMessage}`, this.aemPayload.submitButtonText);
        console.warn(`Error fetching reference vehicles onlineCode for VIM ${this.vinInput}`, e);
      }

      this.checked = false;
      /* until wb 7 upgrade we need double-check here since wb replaced the button attrs when and after loading.
         And we need to make sure its disabled */
      this.checkButtonEnabled();
      setTimeout(() => {
        this.checkButtonEnabled();
      }, 100);
    },
    handleVINErrors(errors: SVRTypes.ErrorResponseObject[]) {
      const inputError = errors[0].error_code as SVRTypes.InputErrorCode;
      if (inputErrorMap[inputError]) {
        this.validationMessage = this.getValidationMessage(
          this.aemPayload[inputErrorMap[inputError].message],
          inputErrorMap[inputError].appendNBA,
        );
        this.trackEvent(
          `Error: ${this.aemPayload[inputErrorMap[inputError].message]}`,
          this.aemPayload.submitButtonText,
        );
      } else {
        const notificationError = errors[0].error_code as SVRTypes.NotificationErrorCode;
        this.errorNotification = this.getValidationMessage(
          this.aemPayload[errorNotificationMap[notificationError].message],
          errorNotificationMap[notificationError].appendNBA,
          this.aemPayload[errorNotificationMap[notificationError].headline],
        );
        this.trackEvent(
          `Error: ${this.aemPayload[errorNotificationMap[notificationError].message]}`,
          this.aemPayload.submitButtonText,
        );
      }
    },
    getCheckboxLabel(): string {
      if (this.aemPayload.privacyLink && this.aemPayload.privacyLinkLabel) {
        return `${this.aemPayload.checkboxLabel} <a class="wb-link wb-link--inline ${
          this.isDarkTheme ? 'wb-link--theme-dark' : ''
        }" href="${this.aemPayload.privacyLink}" target="_blank" rel="noopener">${
          this.aemPayload.privacyLinkLabel
        }</a>`;
      }
      return this.aemPayload.checkboxLabel;
    },
  },
});
